/**
 * Página de la política de privacidad.
 *
 * Es un documento legal, así que la estructura importa tanto como el texto: un
 * encabezado por sección, listas de verdad y un ancho de línea cómodo de leer.
 * Sin esto, quien use un lector de pantalla no puede saltar de sección en
 * sección y tiene que oír el documento entero de arriba abajo.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { idiomaActivo } from '@/i18n';
import { POLITICA, RESPONSABLE, type Bloque } from './textos.ts';

export function Privacidad() {
  const { t } = useTranslation();
  const idioma = idiomaActivo();
  const politica = POLITICA[idioma];

  return (
    <div className="contenedor py-8 sm:py-12">
      <article>
        <h1 className="titulo-pagina">{politica.titulo}</h1>
        <p className="mt-2 text-tinta-700">{politica.intro}</p>

        {politica.secciones.map((seccion) => (
          <section key={seccion.titulo} className="mt-8">
            <h2 className="titulo-seccion">{seccion.titulo}</h2>

            {seccion.anadidaPorDesarrollo ? (
              <p className="aviso mt-3">
                {politica.avisoSeccionAnadida}
              </p>
            ) : null}

            <div className="mt-3 space-y-3">
              {seccion.bloques.map((bloque, indice) => (
                <BloqueTexto key={indice} bloque={bloque} />
              ))}
            </div>
          </section>
        ))}

        <section className="mt-8 border-t border-crema-200 pt-6">
          <h2 className="titulo-seccion">{politica.contactoTitulo}</h2>
          <address className="mt-3 space-y-1 text-[15px] not-italic leading-relaxed text-tinta-700">
            <span className="block font-medium">{RESPONSABLE.nombre}</span>
            <span className="block">{RESPONSABLE.direccion}</span>
            <a
              href={`mailto:${RESPONSABLE.correo}`}
              className="block text-roig-700 underline underline-offset-2"
            >
              {RESPONSABLE.correo}
            </a>
          </address>
          <p className="mt-3 text-sm text-tinta-500">
            <a
              href={RESPONSABLE.autoridadControl}
              // noopener y noreferrer en todo enlace externo: sin ellos, la
              // página de destino puede manipular la nuestra.
              target="_blank"
              rel="noopener noreferrer"
              className="text-roig-700 underline underline-offset-2"
            >
              {RESPONSABLE.autoridadControl.replace('https://', '')}
            </a>
          </p>
        </section>

        <p className="mt-8">
          <Link to="/" className="boton-secundario">
            {politica.volver}
          </Link>
        </p>
      </article>
      <span className="sr-only">{t('app.nom')}</span>
    </div>
  );
}

function BloqueTexto({ bloque }: { bloque: Bloque }) {
  if (bloque.tipo === 'lista') {
    return (
      <ul className="ml-5 list-disc space-y-2">
        {(bloque.elementos ?? []).map((elemento, indice) => (
          <li key={indice} className="text-[15px] leading-relaxed text-tinta-700">
            {elemento}
          </li>
        ))}
      </ul>
    );
  }

  return <p className="text-[15px] leading-relaxed text-tinta-700">{bloque.texto}</p>;
}
